/*
  Public marketing landing page.

  Default route for logged-out visitors. Self-contained: brings its own light
  marketing-aesthetic styling (scoped under .landing-page), its own top nav,
  and hides the dark in-app topbar/footer/bottom-nav while mounted. All CTAs
  funnel to #/signin.

  Sections: hero, trust strip, feature highlights, how-it-works, pricing,
  testimonials, FAQ accordion, footer.
*/
import { navigate, onLeave } from "../router.js";
import { FREE_PRACTICE_LIMIT, FREE_MULTIPLAYER_LIMIT } from "../subscription.js";
import { isInstallable, triggerInstall } from "../install-prompt.js";

export function renderLanding(view) {
  // Toggle a body class so we can hide the dark app chrome while landing
  // is mounted, and restore it when navigating away.
  document.body.classList.add("landing-active");
  hookTeardown();

  // Anonymous visitor counter — fire once per browser session. No PII,
  // no cookies, just bumps a daily aggregate. SessionStorage dedupe stops
  // a tab refresh from double-counting the same person.
  recordLandingHit();

  view.innerHTML = `
    <div class="landing-page">

      <!-- ─── Marketing top nav ─────────────────────────────────────── -->
      <header class="lp-nav">
        <a class="lp-nav-brand" href="#/landing" aria-label="StudyVET home">
          <img src="/assets/icons/icon.svg" alt="" width="36" height="36" />
          <span>Study<span class="lp-accent">VET</span></span>
        </a>
        <nav class="lp-nav-links" aria-label="Page sections">
          <a href="#features">Features</a>
          <a href="#pricing">Pricing</a>
          <a href="#faq">FAQ</a>
        </nav>
        <div class="lp-nav-cta">
          <a class="lp-btn lp-btn-ghost" href="#/signin">Sign in</a>
          <a class="lp-btn lp-btn-primary" href="#/signin" data-cta="hero-nav">Start free</a>
        </div>
      </header>

      <!-- ─── Hero ──────────────────────────────────────────────────── -->
      <section class="lp-hero">
        <div class="lp-hero-inner">
          <div class="lp-hero-copy">
            <span class="lp-eyebrow">NAVLE &middot; VTNE &middot; Veterinary boards</span>
            <h1 class="lp-h1">
              Practice. Review. <span class="lp-h1-accent">Pass.</span>
            </h1>
            <p class="lp-lede">
              The vet-medicine question bank built around how you actually
              remember things. <span data-stat="total" data-fmt="comma">6,200</span>+ board-style questions, a self-grade
              flashcard mode, spaced-repetition review, full-length mock
              exams, and a progress dashboard that tells you exactly where
              to study next.
            </p>
            <div class="lp-hero-ctas">
              <a class="lp-btn lp-btn-primary lp-btn-lg" href="#/signin" data-cta="hero">
                Start free &rarr;
              </a>
              <a class="lp-btn lp-btn-ghost lp-btn-lg" href="#features">
                See features
              </a>
              ${isInstallable() ? `
                <button type="button" class="lp-btn lp-btn-ghost lp-btn-lg" id="lp-install-btn">
                  📲 Install app
                </button>
              ` : ""}
            </div>
            <div class="lp-hero-meta">
              <span class="lp-meta-item">${FREE_PRACTICE_LIMIT} free practice questions</span>
              <span class="lp-meta-item">No card required</span>
              <span class="lp-meta-item">Cancel anytime</span>
            </div>
          </div>
          <!-- Rotating feature carousel — auto-cycles every 4s through
               four real-shape app mockups (subject-mastery radar, 12-week
               heatmap, daily streak, practice question). Tab pills above
               let visitors jump to a specific slide; hover pauses the
               auto-advance so they can read. Built from CSS + inline SVG
               so it stays sharp at every viewport with no image weight.
               JS rotation logic in renderLanding() below. -->
          <div class="lp-hero-art" aria-hidden="false" role="region" aria-label="StudyVET feature preview">
            <div class="lp-hero-glow"></div>

            <div class="lp-hero-tabs" role="tablist">
              <button type="button" class="lp-hero-tab" role="tab" data-slide="0" aria-selected="true">Practice</button>
              <button type="button" class="lp-hero-tab" role="tab" data-slide="1" aria-selected="false">Flashcard</button>
              <button type="button" class="lp-hero-tab" role="tab" data-slide="2" aria-selected="false">Mastery</button>
              <button type="button" class="lp-hero-tab" role="tab" data-slide="3" aria-selected="false">Multiplayer</button>
            </div>

            <div class="lp-hero-stage">
              <!-- Slide 1: Practice question with flip-to-explanation.
                   Front = question + 4 options unanswered. Back = correct
                   answer highlighted + the "why" + bookmark/review chips.
                   Auto-flips every 4.5s; teaches the read-answer-explain
                   loop visually. -->
              <article class="lp-hero-slide is-active" data-slide="0" role="tabpanel" aria-label="Practice question example">
                <div class="lp-slide-head">
                  <span class="lp-slide-eyebrow">Practice · Neurology</span>
                  <span class="lp-slide-meta">Q 14 of 20</span>
                </div>
                <div class="lp-slide-pq-stack">
                  <div class="lp-slide-pq-flipper">
                    <!-- Front: question + 4 options, unanswered -->
                    <div class="lp-slide-pq-front">
                      <p class="lp-slide-pq-question">A 9-year-old neutered male German Shepherd presents with a 4-month history of progressive non-painful pelvic limb ataxia. Reflexes are intact; conscious proprioception is delayed. Most likely diagnosis?</p>
                      <div class="lp-slide-pq-options">
                        <div class="lp-slide-pq-option"><span class="lp-slide-pq-option-letter">A</span><span>Lumbosacral disease</span></div>
                        <div class="lp-slide-pq-option"><span class="lp-slide-pq-option-letter">B</span><span>Hansen type II disc herniation</span></div>
                        <div class="lp-slide-pq-option"><span class="lp-slide-pq-option-letter">C</span><span>Degenerative myelopathy</span></div>
                        <div class="lp-slide-pq-option"><span class="lp-slide-pq-option-letter">D</span><span>Fibrocartilaginous embolism</span></div>
                      </div>
                    </div>
                    <!-- Back: correct answer card + explanation + actions -->
                    <div class="lp-slide-pq-back">
                      <div class="lp-slide-pq-back-head">
                        <span class="lp-slide-pq-correct">Correct</span>
                        <span class="lp-slide-pq-streak">+1 to streak</span>
                      </div>
                      <div class="lp-slide-pq-answer-card">
                        <span class="lp-slide-pq-answer-letter">C</span>
                        <span>Degenerative myelopathy</span>
                      </div>
                      <div class="lp-slide-pq-why">
                        <strong>Why:</strong> Older GSDs presenting with progressive, non-painful UMN paresis and proprioceptive deficits is classic for DM. FCE is acute. Disc disease and lumbosacral disease are typically painful.
                      </div>
                      <div class="lp-slide-pq-rule">
                        <span class="lp-slide-pq-rule-tag">Clinical rule</span>
                        Older large-breed dog + progressive <em>non-painful</em> pelvic limb ataxia &rarr; degenerative myelopathy until proven otherwise. SOD1 mutation test confirms predisposition.
                      </div>
                      <div class="lp-slide-pq-actions">
                        <span class="lp-slide-pq-action">🔖 Bookmark</span>
                        <span class="lp-slide-pq-action">⟳ Adds to review</span>
                      </div>
                    </div>
                  </div>
                </div>
                <p class="lp-slide-foot">Every question reveals the why &mdash; not just the right letter.</p>
              </article>

              <!-- Slide 2: Flashcard mode (Anki-style self-grade with 3D flip) -->
              <article class="lp-hero-slide" data-slide="1" role="tabpanel" aria-label="Flashcard mode example">
                <div class="lp-slide-head">
                  <span class="lp-slide-eyebrow">Flashcard · Infectious Disease</span>
                  <span class="lp-slide-meta">Card 38 of 120 &middot; +6 due</span>
                </div>
                <!-- 3D-flipping card with stacked-deck shadows behind. -->
                <div class="lp-slide-fc-stack">
                  <div class="lp-slide-fc-stack-shadow" aria-hidden="true"></div>
                  <div class="lp-slide-fc-stack-shadow lp-slide-fc-stack-shadow-2" aria-hidden="true"></div>
                  <div class="lp-slide-fc-flipper">
                    <div class="lp-slide-fc-front">
                      <span class="lp-slide-fc-face-eyebrow">Question</span>
                      <div class="lp-slide-fc-q">Drug of choice for canine ehrlichiosis (E. canis)?</div>
                      <div class="lp-slide-fc-hint">Tap to flip</div>
                    </div>
                    <div class="lp-slide-fc-back">
                      <span class="lp-slide-fc-face-eyebrow">Answer</span>
                      <div class="lp-slide-fc-a">
                        <strong>Doxycycline</strong>
                        10 mg/kg PO q24h &times; 28 days. Signs resolve in 24&ndash;48 h.
                      </div>
                      <div class="lp-slide-fc-rule">
                        <span class="lp-slide-fc-rule-tag">Clinical rule</span>
                        Any tick-borne disease in dogs (ehrlichia, anaplasma, Lyme) &rarr; doxycycline. Full 28-day course, even after signs resolve.
                      </div>
                    </div>
                  </div>
                </div>
                <div class="lp-slide-fc-grades" aria-label="Self-grade options">
                  <div class="lp-slide-fc-grade again"><span class="lp-slide-fc-grade-key">1</span>Again</div>
                  <div class="lp-slide-fc-grade hard"><span class="lp-slide-fc-grade-key">2</span>Hard</div>
                  <div class="lp-slide-fc-grade good"><span class="lp-slide-fc-grade-key">3</span>Good</div>
                  <div class="lp-slide-fc-grade easy"><span class="lp-slide-fc-grade-key">4</span>Easy</div>
                </div>
                <p class="lp-slide-foot">StudyVET AI spaces it out automatically &mdash; Good = +6 days, Easy = +2 weeks.</p>
              </article>

              <!-- Slide 3: Subject mastery radar -->
              <article class="lp-hero-slide lp-slide-radar" data-slide="2" role="tabpanel" aria-label="Subject mastery">
                <div class="lp-slide-head">
                  <span class="lp-slide-eyebrow">Subject mastery</span>
                  <span class="lp-slide-meta">avg 42% across top 8</span>
                </div>
                ${radarSvg()}
                <p class="lp-slide-foot">A round shape means even mastery. A spike highlights where to focus next.</p>
              </article>

              <!-- Slide 4: Multiplayer race + weekly leaderboard.
                   Top half: compact head-to-head live race (avatars +
                   live scores + twin progress bars + LIVE indicator
                   + speed-bonus chip). Bottom half: 3-row weekly Elo
                   leaderboard with gold/silver/bronze rank medals.
                   "You" sits at #2 (silver, teal-tinted) between
                   Aleksi (gold) and Maya (bronze) — aspirational
                   social-proof positioning. -->
              <article class="lp-hero-slide" data-slide="3" role="tabpanel" aria-label="Multiplayer race and leaderboard">
                <div class="lp-slide-head">
                  <span class="lp-slide-eyebrow">Multiplayer · Live</span>
                  <span class="lp-slide-meta">Q 7 of 12 &middot; 0:14</span>
                </div>

                <!-- Live race (compact head-to-head) -->
                <div class="lp-slide-mp-match">
                  <div class="lp-slide-mp-vs">
                    <div class="lp-slide-mp-side you">
                      <span class="lp-slide-mp-side-avatar" aria-hidden="true">🐾</span>
                      <div class="lp-slide-mp-side-info">
                        <span class="lp-slide-mp-side-name">You</span>
                        <span class="lp-slide-mp-side-score">5</span>
                      </div>
                    </div>
                    <span class="lp-slide-mp-vs-divider" aria-hidden="true">VS</span>
                    <div class="lp-slide-mp-side opp">
                      <div class="lp-slide-mp-side-info">
                        <span class="lp-slide-mp-side-name">Maya M.</span>
                        <span class="lp-slide-mp-side-score">4</span>
                      </div>
                      <span class="lp-slide-mp-side-avatar" aria-hidden="true">🦊</span>
                    </div>
                  </div>
                  <div class="lp-slide-mp-progress-row">
                    <div class="lp-slide-mp-progress you"><span style="width:71%"></span></div>
                    <div class="lp-slide-mp-progress opp"><span style="width:57%"></span></div>
                  </div>
                  <div class="lp-slide-mp-status">
                    <span class="lp-slide-mp-live">Live</span>
                    <span class="lp-slide-mp-bonus">⚡ Speed bonus +20</span>
                  </div>
                </div>

                <!-- Weekly Elo leaderboard -->
                <div class="lp-slide-mp-board">
                  <div class="lp-slide-mp-board-head">
                    <span>Weekly leaderboard</span>
                    <span class="lp-slide-mp-board-meta">refreshes Sun</span>
                  </div>
                  <div class="lp-slide-mp-board-row gold">
                    <span class="lp-slide-mp-rank">1</span>
                    <span class="lp-slide-mp-board-avatar" aria-hidden="true">🦅</span>
                    <span class="lp-slide-mp-board-name">Aleksi N.</span>
                    <span class="lp-slide-mp-board-elo">1342</span>
                  </div>
                  <div class="lp-slide-mp-board-row silver is-you">
                    <span class="lp-slide-mp-rank">2</span>
                    <span class="lp-slide-mp-board-avatar" aria-hidden="true">🐾</span>
                    <span class="lp-slide-mp-board-name">You</span>
                    <span class="lp-slide-mp-board-elo">1281</span>
                  </div>
                  <div class="lp-slide-mp-board-row bronze">
                    <span class="lp-slide-mp-rank">3</span>
                    <span class="lp-slide-mp-board-avatar" aria-hidden="true">🦊</span>
                    <span class="lp-slide-mp-board-name">Maya M.</span>
                    <span class="lp-slide-mp-board-elo">1247</span>
                  </div>
                </div>

                <p class="lp-slide-foot">Race for points &middot; climb the weekly Elo board.</p>
              </article>

            </div>
          </div>
        </div>
        <div class="lp-trust-strip">
          <span class="lp-trust-icon" aria-hidden="true">🌍</span>
          <span>
            <strong>Trusted</strong> by veterinary students preparing for boards across the
            <strong>US</strong>, <strong>Canada</strong>, <strong>UK</strong>, <strong>India</strong>, and <strong>Australia</strong>.
          </span>
        </div>
      </section>

      <!-- ─── By the numbers ────────────────────────────────────────── -->
      <section class="lp-numbers">
        <div class="lp-numbers-grid">
          <div class="lp-number-cell">
            <div class="lp-number-cell-num">6200<span class="lp-number-unit">+</span></div>
            <div class="lp-number-cell-lbl">board-style questions</div>
          </div>
          <div class="lp-number-cell">
            <div class="lp-number-cell-num">5100<span class="lp-number-unit">+</span></div>
            <div class="lp-number-cell-lbl">flashcards</div>
          </div>
          <div class="lp-number-cell">
            <div class="lp-number-cell-num">140<span class="lp-number-unit">+</span></div>
            <div class="lp-number-cell-lbl">image-based questions</div>
          </div>
          <div class="lp-number-cell">
            <div class="lp-number-cell-num">$3.99<span class="lp-number-unit">/1st mo</span></div>
            <div class="lp-number-cell-lbl">with code <strong>INTRO</strong> &middot; then $6.99/mo</div>
          </div>
        </div>
      </section>

      <!-- ─── Feature highlights ────────────────────────────────────── -->
      <section class="lp-section" id="features">
        <header class="lp-section-head">
          <h2>Everything you need, nothing you don't</h2>
          <p class="lp-section-sub">
            Tools built specifically for veterinary board prep — not retrofitted from a generic flashcard app.
          </p>
        </header>
        <div class="lp-feature-grid">
          ${featureCard("Flashcard mode",
            "Self-grade with Again / Hard / Good / Easy. Built straight on top of the question bank — over [[flashcards]] cards, no separate deck to maintain. Perfect for quick review on a coffee break or between rotations.",
            iconCards(), "lp-feature-hero")}
          ${featureCard("Massive question bank",
            "Over [[total]] board-style questions across small animal, large animal, exotics, and avian. Filter by species, subject, group, or images-only.",
            iconBank())}
          ${featureCard("Spaced repetition that works",
            "Every answer — flashcard or MCQ — feeds the StudyVET AI review queue. Stuff you struggle with comes back tomorrow; stuff you nailed waits weeks. The math does the studying for you.",
            iconBrain())}
          ${featureCard("Full-length mock exams",
            "Timed, balanced across topics, with a question palette and flag-for-review — built to feel like the real thing on test day.",
            iconExam())}
          ${featureCard("Multiplayer races",
            "Quick-match a friend or anyone in the public room. Speed + accuracy ranked Elo-style on weekly and all-time leaderboards.",
            iconBolt())}
          ${featureCard("Target-exam countdown",
            "Set your exam date and StudyVET shows how many days are left, suggests a daily target, and tunes reminders so you peak on test day — not a week early.",
            iconCalendar())}
          ${featureCard("Daily push reminders",
            "Opt-in nudges that remember when your exam is. Skip a day and the reminder copy reflects it; nail your streak and it celebrates.",
            iconBell())}
          ${featureCard("Achievements & streaks",
            "Daily streaks, accuracy badges, and topic-mastery unlocks make consistent study less of a slog.",
            iconTrophy())}
          ${featureCard("Real app on every device",
            "Install once from any browser — no app store, no downloads. Your progress, bookmarks, and exam history sync across phone, tablet, and laptop the moment you sign in.",
            iconPhone())}
        </div>
      </section>

      <!-- ─── How it works ──────────────────────────────────────────── -->
      <section class="lp-section lp-howitworks">
        <header class="lp-section-head">
          <h2>How it works</h2>
        </header>
        <ol class="lp-steps">
          <li>
            <div class="lp-step-num">1</div>
            <h3>Pick what to study</h3>
            <p>Filter the bank by species, subject, or group. Practicing canine cardiology only? You can. Want to drill exotics flashcards on the train? Pick the deck and go.</p>
          </li>
          <li>
            <div class="lp-step-num">2</div>
            <h3>Answer or self-grade</h3>
            <p>Multiple-choice questions show a written explanation, not just the answer letter. Flashcards flip to reveal the answer and let you self-grade Again / Hard / Good / Easy. Bookmark or add a personal note in either mode.</p>
          </li>
          <li>
            <div class="lp-step-num">3</div>
            <h3>Let the review queue do the rest</h3>
            <p>Both flashcards and MCQs feed the same StudyVET AI review queue. Spaced repetition surfaces what you're about to forget — open the app daily and it tells you exactly what to review.</p>
          </li>
        </ol>
      </section>

      <!-- ─── Pricing ───────────────────────────────────────────────── -->
      <section class="lp-section lp-pricing-section" id="pricing">
        <header class="lp-section-head">
          <h2>Free to try, Pro to pass</h2>
          <p class="lp-section-sub">
            Practice mode is free forever — no card, no trial countdown.
            Upgrade when you're ready for the learning science on top.
          </p>
        </header>

        <!-- Free-plan note — moved up here (was below the plan grid) so
             the "no card to try the bank" pitch surfaces immediately
             under the section subtitle, before the paid plans. Centered
             via flex wrapper so it sits dead-center regardless of pill
             content width. -->
        <div class="lp-plan-free-note-wrap">
          <p class="lp-plan-free-note">
            Just want to try the bank? <strong>Free</strong> gets you
            ${FREE_PRACTICE_LIMIT} practice questions, all filters,
            bookmarks, personal notes, ${FREE_MULTIPLAYER_LIMIT} multiplayer races,
            and 15 flashcards &mdash; no card required, nothing expires.
            <a href="#/signin">Start free &rarr;</a>
          </p>
        </div>

        <!-- Intro promo banner — limited-time first-month deal at $3.99
             with the INTRO checkout code. Mirrors the email lifecycle's
             Day-1 intro promo and the by-the-numbers stat strip. -->
        <div class="lp-promo-banner-wrap">
          <div class="lp-promo-banner" role="note" aria-label="Limited-time intro promo">
            <span>
              <strong>First month for $3.99</strong> with code
              <code class="lp-promo-code">INTRO</code> &middot; limited time
            </span>
          </div>
        </div>

        <div class="lp-plan-grid lp-plan-grid-2up">
          <!-- Pro Monthly — calls out the $3.99 INTRO promo on its
               sub-line so the deal is visible from the plan card
               itself, not just the banner. -->
          <article class="lp-plan-card">
            <div class="lp-plan-name">Pro Monthly</div>
            <div class="lp-plan-price">
              <span class="lp-plan-amount">$6.99</span>
              <span class="lp-plan-per">/ month</span>
            </div>
            <p class="lp-plan-sub">
              First month <strong>$3.99</strong> with code
              <code class="lp-promo-code">INTRO</code>.
            </p>
            <a class="lp-btn lp-btn-ghost lp-btn-block" href="#/signin">Get Pro monthly</a>
            <ul class="lp-plan-points">
              <li><strong>Unlimited</strong> practice questions</li>
              <li>Flashcard mode (5,100+ cards)</li>
              <li>Spaced-repetition review queue</li>
              <li>Timed mock exams (customisable)</li>
              <li>Target-exam countdown &amp; daily target</li>
              <li>Exam history &amp; personal bests</li>
              <li>Daily push reminders</li>
              <li>Unlimited multiplayer races</li>
              <li>Full progress dashboard &amp; trends</li>
              <li>Cancel anytime</li>
            </ul>
          </article>

          <!-- Pro Yearly (Best Value). The actual Pro feature set is the
               same as Monthly (isPro() in the client is plan-agnostic),
               so this card differentiates purely on price/commitment:
               save 28%, lock the price for 12 months, no monthly renewal
               friction. Listing the same features twice would be both
               misleading (implying monthly is missing them) and noisy
               so we use "Everything in Pro Monthly" + yearly perks. -->
          <article class="lp-plan-card lp-plan-card-featured">
            <div class="lp-plan-ribbon">Best value</div>
            <div class="lp-plan-name">Pro Yearly</div>
            <div class="lp-plan-price">
              <span class="lp-plan-amount">$59.99</span>
              <span class="lp-plan-per">/ year</span>
            </div>
            <p class="lp-plan-sub">
              Effective <strong>$4.99/month</strong> &mdash; save 28% vs monthly.
            </p>
            <a class="lp-btn lp-btn-primary lp-btn-block" href="#/signin">Get Pro yearly</a>
            <ul class="lp-plan-points">
              <li><strong>Everything in Pro Monthly</strong></li>
              <li>Save 28% &mdash; effective $4.99/month</li>
              <li>Price locked for the full year</li>
              <li>One payment, no monthly renewals</li>
              <li>Cancel anytime</li>
            </ul>
          </article>
        </div>
      </section>

      <!-- ─── Testimonials ──────────────────────────────────────────── -->
      <section class="lp-section lp-testimonials">
        <header class="lp-section-head">
          <h2>What students are saying</h2>
        </header>
        <div class="lp-quote-grid">
          ${quoteCard("The review queue saved me. I stopped re-reading topics I already knew and finally focused on the gaps.", "Priya S.", "4th year, vet school")}
          ${quoteCard("Mock exam mode actually feels like the boards. The flag-for-review and palette are a huge time-saver.", "Marcus L.", "NAVLE prep")}
          ${quoteCard("I do 20 questions on the bus every morning. The streak keeps me honest.", "Aleksi N.", "VTNE candidate")}
        </div>
      </section>

      <!-- ─── FAQ ───────────────────────────────────────────────────── -->
      <section class="lp-section lp-faq" id="faq">
        <header class="lp-section-head">
          <h2>Frequently asked</h2>
        </header>
        <div class="lp-faq-list">
          ${faqItem("Is there a free trial?",
            `Practice mode is free forever and includes ${FREE_PRACTICE_LIMIT} questions plus the full filter set, bookmarks, and notes. No card required and nothing expires. Pro unlocks flashcard mode, the spaced-repetition review queue, mock exams, and the full dashboard.`)}
          ${faqItem("How does flashcard mode work?",
            "Flashcards are generated from the same question bank, so you don't maintain a separate deck. The card shows the stem, you think the answer, then flip to grade yourself — Again / Hard / Good / Easy. Each grade feeds the StudyVET AI so harder cards return sooner and easier ones space out further. Cards that depend on the multiple-choice options are filtered out, leaving roughly 5,100 cards across the full bank.")}
          ${faqItem("Which exams does StudyVET prepare me for?",
            "The bank is targeted at the NAVLE (US/Canada veterinary licensure) and VTNE (veterinary technician) but the topic coverage is broad enough to support BVA-equivalent and Australian board prep too. Filter by subject if you want to focus on a specific exam style.")}
          ${faqItem("Does it work on my phone?",
            "Yes. StudyVET is a Progressive Web App — install it from your phone's browser and it runs full-screen, like a real app. Your account syncs across every device you sign in on, so you can practice on your phone, review on a tablet, and pick up exactly where you left off on a laptop. An internet connection is required to load and save progress.")}
          ${faqItem("Can I cancel?",
            "Anytime, from the account page. You'll keep Pro access until the end of the billing period you've already paid for.")}
          ${faqItem("How is my data handled?",
            "Your progress lives in your StudyVET account on Firebase. Payments are processed by Stripe — we never see your card number.")}
        </div>
      </section>

      <!-- ─── Final CTA ────────────────────────────────────────────── -->
      <section class="lp-section lp-final-cta">
        <div class="lp-final-card">
          <h2>Start studying smarter today</h2>
          <p>Join veterinary students worldwide preparing for boards with StudyVET.</p>
          <a class="lp-btn lp-btn-primary lp-btn-lg" href="#/signin" data-cta="final">
            Create your free account &rarr;
          </a>
          <p class="lp-final-meta">${FREE_PRACTICE_LIMIT} free questions &middot; No card required &middot; Cancel anytime</p>
        </div>
      </section>

      <!-- ─── Footer ───────────────────────────────────────────────── -->
      <footer class="lp-footer">
        <div class="lp-footer-inner">
          <div class="lp-footer-brand">
            <img src="/assets/icons/icon.svg" alt="" width="28" height="28" />
            <span>Study<span class="lp-accent">VET</span></span>
          </div>
          <div class="lp-footer-links">
            <a href="/signin">Sign in</a>
            <a href="/privacy">Privacy</a>
            <a href="/refunds">Refunds &amp; cancellation</a>
            <a href="mailto:support@studyvet.org">Contact</a>
          </div>
          <div class="lp-footer-meta">
            &copy; 2015&ndash;2026 StudyVET. All rights reserved.
          </div>
        </div>
      </footer>

    </div>
  `;

  // Live-update the by-the-numbers cells from the public Cloud Function.
  // Fire-and-forget — the HTML already shows sensible defaults, so a
  // failed or slow fetch never breaks the surface.
  refreshLandingStats(view);

  // FAQ accordion — toggle one open at a time.
  view.querySelectorAll(".lp-faq-item").forEach((item) => {
    const head = item.querySelector(".lp-faq-q");
    head.addEventListener("click", () => {
      const wasOpen = item.classList.contains("open");
      view.querySelectorAll(".lp-faq-item.open").forEach((o) => o.classList.remove("open"));
      if (!wasOpen) item.classList.add("open");
    });
  });

  // Smooth-scroll for in-page anchors so the marketing nav doesn't jump.
  view.querySelectorAll('a[href^="#"]:not([href^="#/"])').forEach((a) => {
    a.addEventListener("click", (e) => {
      const id = a.getAttribute("href").slice(1);
      const tgt = view.querySelector("#" + CSS.escape(id));
      if (tgt) {
        e.preventDefault();
        tgt.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  });

  // Footer legal links — handled via document-level delegation in
  // hookFooterNav() below. Per-element binding inside view's innerHTML
  // gets blown away on re-render and was occasionally not re-attaching
  // on the second visit to the landing page.
  hookFooterNav();

  // Install-app CTA — only rendered when isInstallable() returned true.
  const installBtn = view.querySelector("#lp-install-btn");
  if (installBtn) {
    installBtn.addEventListener("click", async () => {
      installBtn.disabled = true;
      try {
        const status = await triggerInstall();
        if (status === "accepted") {
          window.toast?.("App installed!", "ok");
          installBtn.remove();
        } else if (status === "ios-instructions") {
          window.toast?.(
            "On iPhone: tap the Share icon in Safari, then 'Add to Home Screen'.",
            "info",
          );
        } else if (status === "already-installed") {
          installBtn.remove();
        } else if (status === "unsupported") {
          window.toast?.("Your browser doesn't support app install — try Chrome or Edge.", "warn");
        }
      } finally {
        installBtn.disabled = false;
      }
    });
  }

  // Listen for `beforeinstallprompt` so a button can appear later if the
  // user lingers on the page long enough for Chrome to fire the event.
  // (We don't render the button initially when the event hasn't fired yet
  // because isInstallable() returns false until then.)
  window.addEventListener("beforeinstallprompt", () => {
    // If the button isn't already in the DOM, re-render landing — quickest
    // way to pick it up without bespoke insertion logic.
    if (!document.querySelector("#lp-install-btn")) {
      // Defer slightly so we don't recurse from the inside of the listener.
      setTimeout(() => renderLanding(view), 100);
    }
  }, { once: true });

  // ── Hero carousel rotation ───────────────────────────────────────────
  // Auto-cycles the four feature mockups every 4s with a smooth opacity
  // crossfade (CSS handles the actual transition; JS just toggles the
  // .is-active class + the aria-selected on the matching tab pill).
  // Pauses while the user is hovering the hero art so they have time to
  // read whichever slide is active. Tab clicks jump immediately and
  // reset the timer so the next auto-advance is a full 4s away.
  // Honors prefers-reduced-motion: in that mode the slide swap is
  // instant (CSS forces 0ms transition) and we don't auto-advance,
  // we just let users click tabs to navigate.
  const heroArt = view.querySelector(".lp-hero-art");
  if (heroArt) {
    const slides = Array.from(heroArt.querySelectorAll(".lp-hero-slide"));
    const tabs   = Array.from(heroArt.querySelectorAll(".lp-hero-tab"));
    let activeIdx = 0;
    const INTERVAL_MS = 4500;
    let timerId = null;

    const setActive = (idx) => {
      activeIdx = ((idx % slides.length) + slides.length) % slides.length;
      slides.forEach((s, i) => s.classList.toggle("is-active", i === activeIdx));
      tabs.forEach((t, i) => t.setAttribute("aria-selected", i === activeIdx ? "true" : "false"));
    };

    const advance = () => setActive(activeIdx + 1);

    const start = () => {
      stop();
      // Honor reduced-motion: don't auto-advance; user can still click tabs.
      const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduced) return;
      timerId = setInterval(advance, INTERVAL_MS);
    };
    const stop = () => {
      if (timerId) { clearInterval(timerId); timerId = null; }
    };

    tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        const idx = Number(tab.dataset.slide) || 0;
        setActive(idx);
        start();   // reset the timer so they get a full INTERVAL_MS to read
      });
    });

    // Pause on hover (desktop) and on touch interaction (mobile) so a
    // user reading a slide doesn't have it swap mid-scan.
    heroArt.addEventListener("mouseenter", stop);
    heroArt.addEventListener("mouseleave", start);
    heroArt.addEventListener("touchstart", stop,  { passive: true });
    heroArt.addEventListener("touchend",   start, { passive: true });

    // Pause when the tab is hidden (battery-friendly + spec-aligned).
    // Stash a named ref so onLeave can detach — prior to this fix every
    // render of landing.js piled on another document-level visibilitychange
    // listener (and beforeinstallprompt would re-render landing, stacking
    // duplicates).
    const visHandler = () => {
      if (document.hidden) stop(); else start();
    };
    document.addEventListener("visibilitychange", visHandler);
    onLeave(() => {
      stop();
      document.removeEventListener("visibilitychange", visHandler);
    });

    // Stash the stop fn on the element so the hashchange teardown can
    // clean up the interval when the user navigates away from /landing.
    heroArt._lpCarouselStop = stop;

    start();
  }

  // ── Scroll-reveal animation ──────────────────────────────────────────
  // Tag every section + the numbers strip with .lp-reveal, then swap in
  // .lp-revealed once each crosses 12% of the viewport. Honors
  // prefers-reduced-motion via the CSS rule, which strips the transform
  // and instantly reveals. Disconnected after each element fires so the
  // observer doesn't keep callbacks alive forever.
  const reveals = view.querySelectorAll(".lp-section, .lp-numbers");
  if (reveals.length && typeof IntersectionObserver === "function") {
    reveals.forEach((el) => el.classList.add("lp-reveal"));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("lp-revealed");
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -40px 0px" });
    reveals.forEach((el) => io.observe(el));
  }

  // ── Sticky-nav scroll state ──────────────────────────────────────────
  // Toggle .lp-scrolled on .landing-page once the user has scrolled past
  // the top so the nav can transition into its frosted-blur elevated
  // state. We toggle on the .landing-page wrapper rather than the nav
  // itself so the CSS can chain the descendant selector (.lp-scrolled
  // .lp-nav) and keep all the styling in one place. Uses passive
  // listener + rAF throttle so scroll perf stays smooth.
  const lpRoot = view.querySelector(".landing-page");
  if (lpRoot) {
    // Hysteresis on the toggle threshold — flip ON at 32px, OFF at
    // 12px. Without the gap, mobile rubber-band bounce + momentum
    // overshoot near the very top would whip the class back and forth
    // as scrollY oscillated across a single threshold, which the user
    // saw as the page "shaking" or jittering. The padding transition
    // that used to animate on this state-change has also been removed
    // (CSS, .lp-nav) — only paint-only properties (bg/blur/border)
    // animate now, so even if the class did flicker the layout below
    // wouldn't shift.
    let scrolled = false;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const next = scrolled ? y > 12 : y > 32;
        if (next !== scrolled) {
          scrolled = next;
          lpRoot.classList.toggle("lp-scrolled", scrolled);
        }
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    // Capture the listener on the body class so teardown can clean up.
    lpRoot._lpScrollHandler = onScroll;
    onScroll();   // set initial state
  }
}

/* ─── Section helpers ───────────────────────────────────────────────── */

/* Anonymous landing-hit beacon. Fires the recordLandingHit Cloud Function
   once per browser session. No PII, no cookies — just bumps a daily
   counter on analytics/{YYYY-MM-DD}.landingHits. Failures are silent so
   analytics never blocks the page render. */
const LANDING_HIT_FN_URL =
  "https://us-central1-studyvet-8a301.cloudfunctions.net/recordLandingHit";

function recordLandingHit() {
  try {
    if (sessionStorage.getItem("sv:landingHitSent") === "1") return;
    sessionStorage.setItem("sv:landingHitSent", "1");
  } catch { /* private mode etc. — fall through and just always send */ }

  // Fire-and-forget. keepalive lets it survive even if the user
  // immediately clicks through to /signin.
  try {
    fetch(LANDING_HIT_FN_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ referrer: document.referrer || "" }),
      keepalive: true,
      credentials: "omit",
    }).catch(() => {});
  } catch { /* network may be flat-out unavailable; ignore */ }
}

/* ─── Live landing stats ─────────────────────────────────────────────────
 * Fetches current bank counts (total questions, flashcards generated,
 * image-bearing questions) from the public getLandingStats Cloud Function
 * and updates the by-the-numbers cells. The function is HTTP-cached at
 * the CDN for 6h and stale-while-revalidate for 24h, so most page loads
 * hit the cache and the function itself runs at most a few times an hour
 * worldwide. The HTML already ships sensible defaults (6200/5100/140) so
 * a failed or slow fetch never breaks the surface — values just get
 * upgraded silently when the response arrives. */
const LANDING_STATS_FN_URL =
  "https://us-central1-studyvet-8a301.cloudfunctions.net/getLandingStats";

/* Round down to the nearest `step` so the displayed number is always a
   conservative under-claim of reality (e.g. 6253 → 6200, 5912 → 5900).
   Marketing copy must never overstate. */
function roundDownTo(n, step) {
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.max(step, Math.floor(n / step) * step);
}

async function refreshLandingStats(view) {
  if (!view || typeof fetch !== "function") return;
  try {
    const res = await fetch(LANDING_STATS_FN_URL, { credentials: "omit" });
    if (!res.ok) return;
    const data = await res.json();
    if (!data || typeof data !== "object") return;

    // The 4 number cells are in document order: total questions,
    // flashcards, image questions, price promo. Only update the first
    // three — the price cell is static marketing copy.
    const cells = view.querySelectorAll(".lp-numbers-grid .lp-number-cell-num");
    if (cells.length >= 3) {
      if (Number.isFinite(data.totalQuestions) && data.totalQuestions > 100) {
        const v = roundDownTo(data.totalQuestions, 100);
        cells[0].innerHTML = `${v}<span class="lp-number-unit">+</span>`;
      }
      if (Number.isFinite(data.flashcards) && data.flashcards > 100) {
        const v = roundDownTo(data.flashcards, 100);
        cells[1].innerHTML = `${v}<span class="lp-number-unit">+</span>`;
      }
      if (Number.isFinite(data.imageQuestions) && data.imageQuestions > 10) {
        const v = roundDownTo(data.imageQuestions, 10);
        cells[2].innerHTML = `${v}<span class="lp-number-unit">+</span>`;
      }
    }

    // ── Prose spans ───────────────────────────────────────────────────
    // The hero lede + feature card descriptions also embed live counts
    // via [[total]] / [[flashcards]] / [[images]] tokens. Find every
    // span tagged with data-stat="..." and update its text to the
    // matching live value. Comma format (6,200 not 6200) when
    // data-fmt="comma" — that's the natural form for prose.
    const valueByKey = {
      total:      Number.isFinite(data.totalQuestions) && data.totalQuestions > 100
                    ? roundDownTo(data.totalQuestions, 100) : null,
      flashcards: Number.isFinite(data.flashcards) && data.flashcards > 100
                    ? roundDownTo(data.flashcards, 100) : null,
      images:     Number.isFinite(data.imageQuestions) && data.imageQuestions > 10
                    ? roundDownTo(data.imageQuestions, 10) : null,
    };
    view.querySelectorAll("[data-stat]").forEach((el) => {
      // Skip the big number cells — already handled above with the
      // unit-span pattern. Only update plain text spans.
      if (el.classList.contains("lp-number-cell-num")) return;
      const key = el.dataset.stat;
      const val = valueByKey[key];
      if (val == null) return;
      const useComma = el.dataset.fmt === "comma";
      el.textContent = useComma ? val.toLocaleString("en-US") : String(val);
    });
  } catch { /* silent — defaults remain visible */ }
}

function featureCard(title, body, iconSvg, extraClass = "") {
  // Allow [[total]], [[flashcards]], [[images]] tokens in the body to
  // expand into live-stat spans that refreshLandingStats() can update
  // when the Cloud Function returns. Tokens are inserted AFTER the body
  // is HTML-escaped, so the literal token strings are still safe even
  // if a future caller passes user-provided text.
  const safeBody = esc(body)
    .replaceAll("[[total]]",      '<span data-stat="total"      data-fmt="comma">6,200</span>')
    .replaceAll("[[flashcards]]", '<span data-stat="flashcards" data-fmt="comma">5,100</span>')
    .replaceAll("[[images]]",     '<span data-stat="images"     data-fmt="comma">140</span>');
  return `
    <article class="lp-feature${extraClass ? " " + extraClass : ""}">
      <div class="lp-feature-icon" aria-hidden="true">${iconSvg}</div>
      <h3>${esc(title)}</h3>
      <p>${safeBody}</p>
    </article>`;
}

/* Hero mini-heatmap mock — 12 weeks × 7 days flat, plausible activity
   pattern (sparse early, dense recent, occasional rest day). 84 cells
   total. Each cell gets a level class lvl-1..lvl-4 mapped to a teal
   tint in CSS. Pure data — no live values, just convincing texture
   for the live-feel hero preview. */
function heatmapMockHtml() {
  // Plausible heatmap pattern (84 cells, 0-4 = empty/rest..max activity).
  // Reads as "ramped up over the last 12 weeks" — a marketing-y but
  // honest representation of a typical user's progression.
  const pattern = [
    1,0,1,0,1,0,1, 1,1,0,1,1,0,1, 0,1,1,2,1,2,1,
    2,1,2,2,1,2,2, 2,3,2,3,2,3,2, 3,2,3,3,2,3,3,
    3,4,3,4,3,4,3, 4,3,4,4,3,4,4, 4,3,4,4,4,4,4,
    4,4,4,4,4,4,4, 4,4,4,3,4,4,4, 4,4,4,4,4,4,4,
  ];
  return pattern.map((lvl) =>
    lvl === 0 ? "<span></span>" : `<span class="lvl-${lvl}"></span>`
  ).join("");
}

/* Subject-mastery radar chart — inline SVG. 8 axes (representative vet
   subjects), concentric octagon grid at 25/50/75/100%, mastery polygon
   filled in clinical teal. Static mock values — purely a marketing
   preview, not pulled from live data. Centered in a 320x320 viewBox so
   the SVG element scales cleanly with its container. */
function radarSvg() {
  const subjects = [
    { label: "Internal Med", value: 0.62 },
    { label: "Anesthesia",    value: 0.48 },
    { label: "Cardiology",    value: 0.55 },
    { label: "Microbiology",  value: 0.32 },
    { label: "Imaging",       value: 0.28 },
    { label: "Anat/Phys",     value: 0.42 },
    { label: "Dermatology",   value: 0.38 },
    { label: "Emergency",     value: 0.50 },
  ];
  const cx = 160, cy = 160, rMax = 110;
  const axes = subjects.length;

  // Helper: convert (axisIndex, t in 0..1) → {x, y}.
  // Axes start at 12 o'clock and go clockwise.
  const point = (i, t) => {
    const angle = (Math.PI * 2 * i) / axes - Math.PI / 2;
    return {
      x: cx + Math.cos(angle) * rMax * t,
      y: cy + Math.sin(angle) * rMax * t,
    };
  };
  // Where the label sits (just outside the chart).
  const labelPoint = (i) => point(i, 1.22);

  // Concentric ring polygons at 25/50/75/100% — these form the grid.
  const ringPoly = (t) =>
    Array.from({ length: axes }, (_, i) => {
      const p = point(i, t);
      return `${p.x.toFixed(1)},${p.y.toFixed(1)}`;
    }).join(" ");

  // Mastery polygon — connect each subject's value point.
  const masteryPoly = subjects
    .map((s, i) => {
      const p = point(i, s.value);
      return `${p.x.toFixed(1)},${p.y.toFixed(1)}`;
    })
    .join(" ");

  // Spoke axes from center to outer ring.
  const axesLines = Array.from({ length: axes }, (_, i) => {
    const p = point(i, 1);
    return `<line class="lp-radar-axis" x1="${cx}" y1="${cy}" x2="${p.x.toFixed(1)}" y2="${p.y.toFixed(1)}"/>`;
  }).join("");

  // Subject labels — positioned at 1.22r so they don't overlap the
  // outer ring. text-anchor adapts to which side of the chart they're on.
  const labels = subjects
    .map((s, i) => {
      const p = labelPoint(i);
      // Snap text-anchor based on x relative to center to avoid overlap.
      const anchor =
        Math.abs(p.x - cx) < 6 ? "middle" : p.x > cx ? "start" : "end";
      const dy = p.y < cy - 10 ? -2 : p.y > cy + 10 ? 8 : 4;
      return `<text class="lp-radar-label" x="${p.x.toFixed(1)}" y="${(p.y + dy).toFixed(1)}" text-anchor="${anchor}">${esc(s.label)}</text>`;
    })
    .join("");

  // Mastery dots at each vertex.
  const dots = subjects
    .map((s, i) => {
      const p = point(i, s.value);
      return `<circle class="lp-radar-dot" cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="3"/>`;
    })
    .join("");

  return `
    <svg viewBox="0 0 320 320" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Subject mastery radar showing eight veterinary subjects">
      <polygon class="lp-radar-grid" points="${ringPoly(0.25)}"/>
      <polygon class="lp-radar-grid" points="${ringPoly(0.50)}"/>
      <polygon class="lp-radar-grid lp-radar-grid-mid" points="${ringPoly(0.75)}"/>
      <polygon class="lp-radar-grid" points="${ringPoly(1.00)}"/>
      ${axesLines}
      <polygon class="lp-radar-fill" points="${masteryPoly}"/>
      ${dots}
      ${labels}
      <text class="lp-radar-tick" x="${cx + 6}" y="${cy - rMax * 0.5 + 3}">50%</text>
      <text class="lp-radar-tick" x="${cx + 6}" y="${cy - rMax + 3}">100%</text>
    </svg>
  `;
}

function quoteCard(quote, name, role) {
  // Initial-letter avatar — matches the calm-clinical "by the numbers"
  // design language. No external image, scales cleanly at any size.
  const initials = String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();
  return `
    <figure class="lp-quote">
      <blockquote>${esc(quote)}</blockquote>
      <figcaption>
        <span class="lp-quote-avatar" aria-hidden="true">${esc(initials)}</span>
        <span class="lp-quote-meta">
          <strong>${esc(name)}</strong>
          <span class="lp-quote-role">${esc(role)}</span>
        </span>
      </figcaption>
    </figure>`;
}

function faqItem(q, a) {
  return `
    <details class="lp-faq-item">
      <summary class="lp-faq-q">
        <span>${esc(q)}</span>
        <span class="lp-faq-chev" aria-hidden="true">+</span>
      </summary>
      <div class="lp-faq-a">${esc(a)}</div>
    </details>`;
}

/* ─── Inline icons (kept here so the view is self-contained) ─────────────
   Phosphor / Lucide-inspired set — slightly thinner stroke (1.5) and larger
   visual size (32px) for a cleaner, more contemporary feel. Each glyph has
   a subtle interior detail so it reads as a real icon, not just an outline. */

const ICON_PROPS = `viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"`;

// Two layered flashcards, the front one with content lines and a subtle
// "flip corner" notch at top-right.
function iconCards() {
  return `<svg ${ICON_PROPS}>
    <rect x="3" y="6" width="12.5" height="14" rx="2.5" transform="rotate(-8 9.25 13)"/>
    <path d="M19 5h-7a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2V8l-3-3z"/>
    <path d="M19 5v3h3"/>
    <path d="M13 11h5M13 14h3"/>
  </svg>`;
}

// Stacked book-spine library — represents the question bank as a real
// collection rather than a generic "building" silhouette.
function iconBank() {
  return `<svg ${ICON_PROPS}>
    <path d="M4 19.5V5.5A2 2 0 0 1 6 3.5h13a1 1 0 0 1 1 1V18"/>
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
    <path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20v-4"/>
    <path d="M8.5 7.5h6"/>
  </svg>`;
}

// Refresh-cycle with two opposing arrows — visually conveys the
// "comes back tomorrow / spaces out further" idea of SM-2 better than
// a literal brain.
function iconBrain() {
  return `<svg ${ICON_PROPS}>
    <path d="M3.5 12a8.5 8.5 0 0 1 14.7-5.85L21 8.5"/>
    <path d="M21 3.5V8.5h-5"/>
    <path d="M20.5 12a8.5 8.5 0 0 1-14.7 5.85L3 15.5"/>
    <path d="M3 20.5V15.5h5"/>
  </svg>`;
}

// Clipboard with checkmark — clearer "completed test" cue than a sheet
// of horizontal lines.
function iconExam() {
  return `<svg ${ICON_PROPS}>
    <path d="M9 4h6a1 1 0 0 1 1 1v1.5H8V5a1 1 0 0 1 1-1z"/>
    <path d="M16 5.5h2.5A1.5 1.5 0 0 1 20 7v13.5A1.5 1.5 0 0 1 18.5 22h-13A1.5 1.5 0 0 1 4 20.5V7a1.5 1.5 0 0 1 1.5-1.5H8"/>
    <path d="M8.5 13.5l2.5 2.5 4.5-5"/>
  </svg>`;
}

// Two avatars in a "vs." stance with a divider — reads as multiplayer
// far better than a generic lightning bolt did.
function iconBolt() {
  return `<svg ${ICON_PROPS}>
    <circle cx="7.5" cy="9" r="2.5"/>
    <path d="M3.5 18a4 4 0 0 1 8 0"/>
    <circle cx="16.5" cy="9" r="2.5"/>
    <path d="M12.5 18a4 4 0 0 1 8 0"/>
    <path d="M12 4v16" stroke-dasharray="2 2"/>
  </svg>`;
}

// Calendar with a target ring + filled centre — stronger "exam-day target"
// metaphor than a grid of dots.
function iconCalendar() {
  return `<svg ${ICON_PROPS}>
    <rect x="3" y="5" width="18" height="16.5" rx="2.5"/>
    <path d="M3 10h18M8 3v4M16 3v4"/>
    <circle cx="12" cy="15.5" r="2.8"/>
    <circle cx="12" cy="15.5" r="0.6" fill="currentColor" stroke="none"/>
  </svg>`;
}

// Modern softly-domed bell with clapper.
function iconBell() {
  return `<svg ${ICON_PROPS}>
    <path d="M5.5 17h13l-1.6-2.4A4 4 0 0 1 16 12.5V10a4 4 0 0 0-8 0v2.5a4 4 0 0 1-.9 2.1L5.5 17z"/>
    <path d="M10 20a2 2 0 0 0 4 0"/>
  </svg>`;
}

// Trophy with cleaner side handles and a base.
function iconTrophy() {
  return `<svg ${ICON_PROPS}>
    <path d="M8.5 21h7M12 17.5v3.5"/>
    <path d="M7 4h10v6a5 5 0 0 1-10 0V4z"/>
    <path d="M17 5.5h2.5a1.5 1.5 0 0 1 0 3H17M7 5.5H4.5a1.5 1.5 0 0 0 0 3H7"/>
  </svg>`;
}

// Phone with a small dot speaker, rounded chassis, and home pill.
function iconPhone() {
  return `<svg ${ICON_PROPS}>
    <rect x="6.5" y="2.5" width="11" height="19" rx="2.5"/>
    <path d="M10.5 18.5h3"/>
    <path d="M11 5.5h2"/>
  </svg>`;
}

/* ─── Teardown: remove the body class when leaving the landing route ── */

let teardownHooked = false;
function hookTeardown() {
  if (teardownHooked) return;
  teardownHooked = true;
  window.addEventListener("hashchange", () => {
    const onLanding = location.hash.startsWith("#/landing") || location.hash === "" || location.hash === "#";
    if (!onLanding) {
      document.body.classList.remove("landing-active");
      // Clean up the sticky-nav scroll listener we attached in
      // renderLanding so it doesn't fire forever on every other view.
      const lpRoot = document.querySelector(".landing-page");
      if (lpRoot && lpRoot._lpScrollHandler) {
        window.removeEventListener("scroll", lpRoot._lpScrollHandler);
        delete lpRoot._lpScrollHandler;
      }
      // Stop the hero carousel auto-advance interval — would otherwise
      // keep firing on whatever route we navigated to (cheap but
      // pointless work + a dangling timer).
      const heroArt = document.querySelector(".lp-hero-art");
      if (heroArt && heroArt._lpCarouselStop) {
        heroArt._lpCarouselStop();
        delete heroArt._lpCarouselStop;
      }
    } else {
      document.body.classList.add("landing-active");
    }
  });
}

let footerNavHooked = false;
function hookFooterNav() {
  if (footerNavHooked) return;
  footerNavHooked = true;
  // Document-level delegation: survives every view re-render. Catches
  // clicks on any [data-lp-route] anchor anywhere in the app and forces
  // a hard navigation to the canonical hash-route URL. We bind exactly
  // once at module level to avoid stacking duplicate listeners on every
  // landing render.
  document.addEventListener("click", (e) => {
    const a = e.target && e.target.closest && e.target.closest("[data-lp-route]");
    if (!a) return;
    e.preventDefault();
    window.location.href = window.location.origin + "/#" + a.dataset.lpRoute;
  });
}

function esc(s) {
  return String(s ?? "")
    .replaceAll("&","&amp;").replaceAll("<","&lt;").replaceAll(">","&gt;");
}
